import { convertPrice } from './price'
import { addSalesTax } from './payment'

import { IRegisterTab } from '../models/event-registered-users/event-registered-users.interface'
import { IRegistrationByDayInterface } from '../models/registrations-by-day/registrationByDay.interface'
import { IRegistrationFeesInterface } from '../models/registration-fees/registrationFees.interface'
import { IRegistrationTicketInterface } from '../models/registration-tickets/registrationTicket.interface'

interface IRegistrationByDayInterfaceExtended extends IRegistrationByDayInterface {
  isChildren?: boolean
}
export const getRegistrations = (registerTabData: IRegisterTab[] | null) => {
  const parentRegistrations: IRegistrationByDayInterface[] = []
  const childrenRegistrations: IRegistrationByDayInterfaceExtended[] = []

  registerTabData?.forEach((register) => {
    register.registrationsByDay.forEach((registrationByDay) => {
      parentRegistrations.push(registrationByDay)
    })
    register.children?.forEach((children) => {
      children.registrationsByDay.forEach((registrationByDay) => {
        childrenRegistrations.push({ ...registrationByDay, isChildren: true })
      })
    })
  })

  return {
    parentRegistrations: parentRegistrations,
    childrenRegistrations: childrenRegistrations as IRegistrationByDayInterfaceExtended[],
    allRegistrations: [
      ...parentRegistrations,
      ...childrenRegistrations,
    ] as IRegistrationByDayInterfaceExtended[],
  }
}

export const getAllItems = ({
  registrationsByDay,
  fees,
  tickets,
  chargeSalesTax,
}: {
  registrationsByDay: IRegistrationByDayInterface[]
  fees: IRegistrationFeesInterface[]
  tickets: IRegistrationTicketInterface[]
  chargeSalesTax: boolean
}) => {
  const allItems = [
    ...registrationsByDay.map((item) => {
      const price =
        convertPrice(item.registrationPrice) + (item.isQualify ? convertPrice(item.qualifyFee) : 0)
      return {
        ...item,
        price: price / 100,
        selectedUnitsCount: 1,
        ownerId: item.riderId,
        ownerName: item.riderName,
        ownerProfilePicture: item.riderProfilePicture,
      }
    }),
    ...fees.map((item) => ({
      ...item,
      price: item.feesPrice,
      selectedUnitsCount: item.selectedUnitsCount,
      ownerId: item.ownerId,
      ownerName: item.ownerName,
      ownerProfilePicture: item.ownerProfilePicture,
    })),
    ...tickets.map((item) => ({
      ...item,
      price: item.ticketPrice,
      selectedUnitsCount: item.selectedUnitsCount,
      ownerId: item.ownerId,
      ownerName: item.ownerName,
      ownerProfilePicture: item.ownerProfilePicture,
    })),
  ]

  const paymentsByRecipient: {
    [key: string]: {
      recipientName: string
      recipientPicture: string
      assigned: number
      billed: number
      amountToPay: number
    }
  } = {}

  // First pass: calculate 'assigned' for all owners
  allItems.forEach((item) => {
    if (!item.ownerId) return

    if (!paymentsByRecipient[item.ownerId]) {
      paymentsByRecipient[item.ownerId] = {
        recipientName: item.ownerName || '',
        recipientPicture: item.ownerProfilePicture || '',
        assigned: 0,
        billed: 0,
        amountToPay: 0,
      }
    }

    // Increment assigned count for this owner
    paymentsByRecipient[item.ownerId].assigned += 1
  })

  // Second pass: calculate 'billed' and 'total' for recipients
  allItems.forEach((item) => {
    if (!item.recipientId) return

    if (!paymentsByRecipient[item.recipientId]) {
      paymentsByRecipient[item.recipientId] = {
        recipientName: item.recipientName || '',
        recipientPicture: item.recipientProfilePicture || '',
        assigned: 0, // assigned might already be > 0 if the user is also an owner
        billed: 0,
        amountToPay: 0,
      }
    }

    const total =
      convertPrice(item.price) * (item.selectedUnitsCount || 1) +
      addSalesTax(
        item.created,
        convertPrice(item.price) * (item.selectedUnitsCount || 1),
        item.includeSalesTax,
        chargeSalesTax
      )

    // Add total price with sales tax if applicable
    paymentsByRecipient[item.recipientId].amountToPay += total
    // Increment billed count
    paymentsByRecipient[item.recipientId].billed += 1
  })

  const amountByPerson = Object.entries(paymentsByRecipient).map(([id, data]) => ({
    ...data,
    recipientId: id,
    amountToPay: data.amountToPay / 100,
  }))

  return { allItems, amountByPerson }
}
